import { Button, Tooltip } from "@chakra-ui/react";
import {
  permissionFor,
  FeatureAccess,
} from "@syla/shared/types/helpers/permissions";
import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { downloadHoldingStatementCsv } from "../../../api/user/downloadHoldingStatementCsv";
import { AccountTimeZone } from "../../../constants/AccountTimeZone";
import { formatDateLocal } from "../../../helpers/formatDateLocal";
import { useReportError } from "../../../helpers/useReportError";
import { Account } from "../../../types/Account";
import { addDaysInTimezone } from "../../atoms/datePicker/tzDateUtils";

export function DownloadHoldingStatement({
  account,
  byAsset,
  withMarketValue,
  balancesDate,
}: {
  account: Account | undefined;
  byAsset: boolean;
  withMarketValue: boolean;
  balancesDate: Date | undefined;
}) {
  const reportError = useReportError();
  const [isDownloading, setIsDownloading] = useState(false);

  const { hasPermission } = permissionFor({
    feature: FeatureAccess.GetHoldingStatement,
    transactionCount: 0, // this isn't right, it should be provided
    subscriptions: account?.subscriptions ?? [],
    account,
  });

  return (
    <Tooltip
      shouldWrapChildren
      label={holdingStatementTooltip(hasPermission, balancesDate)}
    >
      <Button
        backgroundColor="white"
        size="sm"
        variant="outline"
        leftIcon={<FiDownload />}
        isDisabled={!hasPermission || !account}
        isLoading={isDownloading}
        onClick={(event) => {
          event.stopPropagation();
          setIsDownloading(true);
          downloadHoldingStatementCsv({
            accountId: account!._id,
            byAsset,
            marketValue: withMarketValue,
            balanceDate: balancesDate,
          })
            .catch((error) => {
              reportError(error, {
                title: "Download failed",
              });
            })
            .finally(() => setIsDownloading(false));
        }}
      >
        Download Holding Statement
      </Button>
    </Tooltip>
  );
}

function holdingStatementTooltip(
  isFeatureAvailable: boolean,
  balancesDate: Date | undefined,
) {
  if (!isFeatureAvailable) {
    return "Holding Statements are only available on an Assurance or higher plan.";
  }

  if (balancesDate) {
    // balancesDate has already been adjusted by +1 day to be the very start of
    // the following day, so we must subtract one day to get back to the day selected.
    const selectedDate = addDaysInTimezone(balancesDate, -1, AccountTimeZone);
    return `Download a CSV Holding Statement showing your holdings at the end of day on ${formatDateLocal(
      selectedDate,
      {
        timeZone: AccountTimeZone,
      },
    )}.`;
  }

  return "Download a CSV Holding Statement showing your current holdings.";
}
